const PROTECTED_BLOCK_PATTERN = /<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi;
const HTML_COMMENT_PATTERN = /<!--[\s\S]*?-->/g;
const PLACEHOLDER_SENTINEL = '\u0000';
const PLACEHOLDER_PATTERN = /\u0000(\d+)\u0000/g;

/**
 * Froala persists its toolbar markup into saved HTML as commented-out icon
 * elements — `<!-- <i class="fas fa-align-left" aria-hidden="true"></i> -->` —
 * one per toolbar button, repeated for every editable block. On
 * /offering/gigawatt that is 9,576 comments totalling 585 KB, which is 56% of
 * the delivered page and the reason Semrush flags it for HTML size. Comments
 * never render, so dropping them changes no pixel.
 *
 * `<script>` and `<style>` bodies are masked first: the legacy
 * `<style><!-- ... --></style>` hiding idiom puts real CSS inside comment
 * markers, and stripping those would delete the rules rather than the chrome.
 */
const stripHtmlComments = (html) => {
  const protectedBlocks = [];
  const masked = html.replace(PROTECTED_BLOCK_PATTERN, (block) => {
    protectedBlocks.push(block);
    return `${PLACEHOLDER_SENTINEL}${protectedBlocks.length - 1}${PLACEHOLDER_SENTINEL}`;
  });

  return masked
    .replace(HTML_COMMENT_PATTERN, '')
    .replace(PLACEHOLDER_PATTERN, (_, index) => protectedBlocks[Number(index)]);
};

/**
 * Strip Froala editor chrome from issuer-authored pitch deck / campaign HTML.
 *
 * The Froala WYSIWYG editor used in issuer_webapp persists its own
 * editor-mode attributes into the saved HTML — notably
 * `contenteditable="true"` on `<div class="fr-element fr-view">` wrappers.
 * When that HTML is rendered raw via `dangerouslySetInnerHTML` on a public
 * page, those attributes turn the body content into a live edit surface so
 * any visitor can type into it (see PD-34534b). Strip the attribute on the
 * way in so the discover side always sees inert markup.
 *
 * @param {string} html  Raw HTML string (issuer-authored, untrusted shape).
 * @returns {string} Same HTML with editor chrome removed and legacy links normalized.
 */
export const stripFroalaEditorChrome = (html) => {
  if (!html || typeof html !== 'string') return html;
  return stripHtmlComments(html)
    .replace(/\s+contenteditable(?:\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+))?/gi, '')
    .replace(/\s+href=(["'])(https?:\/\/[^"']*)\1/gi, (match, quote, rawUrl) => {
      const cleanedUrl = rawUrl.replace(/\u00a0/g, '').trim();
      try {
        const parsed = new URL(cleanedUrl);
        const hostname = parsed.hostname.toLowerCase();
        const hasValidHostname = hostname === 'localhost' || hostname.includes('.');
        if (!hasValidHostname) return '';

        if (hostname === 'startengine.com' || hostname === 'www.startengine.com') {
          parsed.protocol = 'https:';
          parsed.hostname = 'www.startengine.com';
          return ` href=${quote}${parsed.toString()}${quote}`;
        }

        return ` href=${quote}${cleanedUrl}${quote}`;
      } catch {
        return '';
      }
    })
    .replace(
      /\b(src|data-src)=(["'])http:\/\/gen\.sendtric\.com\//gi,
      '$1=$2https://gen.sendtric.com/',
    );
};

export default stripFroalaEditorChrome;
